import { setBrandListMetadata } from "@iewc/Utilities/Metadata";
import { trackPageChange } from "@iewc/Utilities/tracking";
import setPageMetadata from "@insite/client-framework/Common/Utilities/setPageMetadata";
import { HasDeferredApiContext, withCallDeferredApi } from "@insite/client-framework/Components/DeferredApiContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getSettingsCollection,
    getShouldPreventCatalogLoop,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getAllBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadAllBrands from "@insite/client-framework/Store/Pages/Brands/Handlers/LoadAllBrands";
import loadBrandsAlphabet from "@insite/client-framework/Store/Pages/Brands/Handlers/LoadBrandsAlphabet";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Modals from "@insite/content-library/Components/Modals";
import Page from "@insite/mobius/Page";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const allBrandsDataView = getAllBrandsDataView(state);
    const page = getCurrentPage(state);
    const location = getLocation(state);
    const settingsCollection = getSettingsCollection(state);
    const catalogPageState = getCatalogPageStateByPath(state, location.pathname);

    return {
        shouldLoadBrandAlphabet:
            !state.pages.brands.brandAlphabetState.value && !state.pages.brands.brandAlphabetState.isLoading,
        shouldLoadAllBrandList: !allBrandsDataView.value && !allBrandsDataView.isLoading,
        deferredApiCalls: state.context.deferredApiCalls,
        allBrandsDataView,
        page,
        location,
        websiteName: state.context.website.name,
        websiteSettings: settingsCollection.websiteSettings,
        currentUrl: state.context.website.uri.split("/api")[0],
        catalogPageState,
    };
};

const mapDispatchToProps = {
    loadBrandsAlphabet,
    loadAllBrands,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasShellContext &
    PageProps &
    HasDeferredApiContext;

class BrandsPage extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        const { loadBrandsAlphabet, shouldLoadBrandAlphabet, shouldLoadAllBrandList, loadAllBrands } = this.props;
        if (shouldLoadBrandAlphabet && !getShouldPreventCatalogLoop(this.props.deferredApiCalls)) {
            loadBrandsAlphabet();
        }
        if (shouldLoadAllBrandList && !getShouldPreventCatalogLoop(this.props.deferredApiCalls)) {
            loadAllBrands({
                sort: "name asc",
                select: "id,detailPagePath,name",
            });
        }

        this.trySetMetadata();
    }

    componentDidUpdate(): void {
        this.trySetMetadata();
    }

    trySetMetadata() {
        const {
            allBrandsDataView,
            location,
            page,
            websiteName,
            websiteSettings,
            currentUrl,
            catalogPageState,
            shellContext: { isEditing, isInShell },
        } = this.props;

        if (!page || !websiteSettings || !websiteName || !allBrandsDataView.value) {
            return false;
        }

        if (isEditing || isInShell) {
            return;
        }

        trackPageChange(page.type);       

        const pageMetaData = {
            metaDescription: "",
            metaKeywords: "",
            openGraphImage: "",
            openGraphTitle: "",
            openGraphUrl: "",
            currentPath: location.pathname,
            canonicalPath: "",
            title: page.name || "Brands",
            websiteName,
            alternateLanguageUrls: undefined,
        };

        setPageMetadata(pageMetaData, websiteSettings);

        // Verndale customization - ticket: IEIXOP-4325
        setTimeout(() => {
            setBrandListMetadata({ allBrandsDataView, currentUrl, currentPage: page, location, catalogPageState });
        }, 500);
        // End Verndale customization

        return true;
    }

    render() {
        return (
            <Page>
                <Zone contentId={this.props.id} zoneName="Content" />
                <Modals />
            </Page>
        );
    }
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withCallDeferredApi(withIsInShell(BrandsPage))),
    definition: {
        hasEditableUrlSegment: false,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

export const BrandsPageContext = "BrandsPage";
